import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TagsApi } from '@app/tags/tag-api.model';
import { PartialUserPosition } from '@app/onboarding/onboarding-api.model';
import { ExperienceDateService } from '@app/onboarding/services/experience-date.service';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ExperienceModalComponent } from '@app/user-content/user-input/experience-modal/experience-modal.component';
import { UserPosition } from '@app/inputs/inputs-api.model';
import { camelCaseKeys } from '@app/shared/utils/property';
/**
 * Display the experiece parsed from resume
 * @example
 * ```
 * <dgx-experience [experience]="experience">
 * </dgx-experience>
 */
@Component({
  selector: 'dgx-experience',
  templateUrl: './experience.component.html',
  styleUrls: ['./experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExperienceComponent implements OnInit {
  @Input() public experience: PartialUserPosition;
  public i18n = this.translate.instant([
    'Core_Skill',
    'Core_Skills',
    'Core_Remove',
    'Core_Edit',
    'PositionFormCtrl_EditExperience',
  ]);

  public tags: TagsApi.Tag[] = [];
  constructor(
    private translate: TranslateService,
    private experienceDateService: ExperienceDateService,
    private onboardingService: OnboardingService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.tags = this.experience?.tags;
  }

  public editJob(): void {
    const inputs = {
      input: this.experience, // defaults
      initialViewModel: this.experience,
      inputId: this.experience.inputId,
      userInputId: this.experience.userInputId,
      userPosition: this.experience,
      isCompleting: false,
      isEditing: false,
      modalTitle: this.i18n.PositionFormCtrl_EditExperience,
    };

    this.modalService
      .show<UserPosition>(ExperienceModalComponent, {
        windowClass: 'lg-modal',
        inputs,
      })
      .subscribe((experience) => {
        // Tags coming back from experience modal are in PascalCasing
        experience = camelCaseKeys(experience);
        this.onboardingService.editJobRole(this.experience, experience);
        this.experience = experience;
        this.cdr.detectChanges();
      });
  }

  public removeJob(): void {
    this.onboardingService.removeJobRole(this.experience);
  }

  /**
   * Remove a skill from a experience.
   *
   * @param tag Skill to remove from experiece
   */
  public removeSkill(tag: TagsApi.Tag): void {
    this.onboardingService.removeJobRoleSkill(this.experience, tag);
  }

  /**
   * Returns a formatted string for organization name and date so that the ellipsis in the view
   * can truncate the text accordingly
   *   */
  public getSubHeading(): string {
    if (!this.experience.organizationName) return '';
    return `${this.experience.organizationName} \u2022 ${this.buildDate()}`;
  }

  /**
   * Create a label with a total count of the number of skills
   *
   * @param tags - the list of tags.
   */
  public getPillSkillCount(tags: TagsApi.Tag[]): string {
    const plural =
      tags.length === 1 ? this.i18n.Core_Skill : this.i18n.Core_Skills;
    return `${tags.length} ${plural}`;
  }

  public buildDate(): string {
    return this.experienceDateService.buildDate(this.experience);
  }
}
